import React, { useState } from 'react';
import { PlusIcon, TrashIcon, ShieldIcon, CreditCardIcon, BanknoteIcon, SmartphoneIcon, WalletIcon } from 'lucide-react';
import { Card, StepHeading, BlockLabel, Toggle, Helper } from '../SettingsUI';
import { useBusiness } from '../../BusinessContext';
import { InviteMemberModal, TeamMember, ROLE_PRESETS, accessSummary } from '../../InviteMemberModal';
import { UpgradePlanModal } from '../../UpgradePlanModal';
const INITIAL_MEMBERS: TeamMember[] = [{
  name: 'Fadi',
  role: 'Admin',
  email: 'fadi@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Admin')!.access,
  canViewContact: true
}, {
  name: 'Sara',
  role: 'Sales',
  email: 'sara@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Sales')!.access,
  canViewContact: true
}, {
  name: 'Sales Team',
  role: 'Viewer',
  email: 'team@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Viewer')!.access,
  canViewContact: false
}];
export function TeamMembers() {
  const [members, setMembers] = useState<TeamMember[]>(INITIAL_MEMBERS);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const saveMember = (m: TeamMember) => {
    setMembers(prev => {
      const idx = prev.findIndex(x => x.email === m.email && x.email !== '');
      if (idx >= 0) return prev.map((x, i) => i === idx ? m : x);
      return [...prev, m];
    });
    setInviteOpen(false);
  };
  return <div className="space-y-5" data-id="element-3620" data-mp="2ysyy4d7pna1j">
      <StepHeading title="Team Members" desc="Invite and manage who can access this hub." data-id="element-3621" data-mp="2yu1lib5tqucp" />
      <Card data-id="element-3622" data-mp="32vvhw78zlc72">
        <div className="flex items-center justify-end mb-4" data-id="element-3623" data-mp="37sdl32oo52ha">
          <button onClick={() => {
          setEditingMember(null);
          setInviteOpen(true);
        }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-indigo text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3624" data-mp="37tx3aytvxnlp">
            <PlusIcon className="w-4 h-4" data-id="element-3625" data-mp="2f47oatxr20t6" />
            Invite
          </button>
        </div>
        <div className="space-y-2.5" data-id="element-3626" data-mp="2t3p3qip5o1pv">
          {members.map(m => <div key={m.email || m.name} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3" data-id="element-3627" data-mp="2t1miebyy6wrh">
              <div className="flex items-center gap-3 min-w-0" data-id="element-3628" data-mp="20fkxnw2o9mmn">
                <div className="w-9 h-9 rounded-full bg-primary-indigo text-white font-bold flex items-center justify-center text-sm flex-shrink-0" data-id="element-3629" data-mp="1vggnb240hiuq">
                  {m.name.charAt(0)}
                </div>
                <div className="min-w-0" data-id="element-3630" data-mp="25duro3cnuh6o">
                  <p className="font-semibold text-dark-navy text-sm truncate" data-id="element-3631" data-mp="25c1m6rdsa309">
                    {m.name}
                  </p>
                  <p className="text-xs text-slate-500 truncate" data-id="element-3632" data-mp="1cpqllgq6g23g">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3633" data-mp="3ockd31luyjnz">
                <span className="text-xs font-semibold text-slate-600 px-2 py-1 bg-slate-50 border border-slate-200 rounded-md" data-id="element-3634" data-mp="3t3ixoddvq3x1">
                  {m.role}
                </span>
                <span className="hidden lg:inline text-xs text-slate-400" data-id="element-3635" data-mp="3fa1e4sl70r70">
                  {accessSummary(m.access)}
                </span>
                <button onClick={() => {
              setEditingMember(m);
              setInviteOpen(true);
            }} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-lg transition-colors duration-150 ease-out" title="Edit access" data-id="element-3636" data-mp="axee0e7ouna5">
                  <ShieldIcon className="w-4 h-4" data-id="element-3637" data-mp="6y5ip4x02hch" />
                </button>
                {m.role !== 'Admin' && <button onClick={() => setMembers(prev => prev.filter(x => x.email !== m.email))} className="p-1.5 text-slate-400 hover:text-error hover:bg-error/5 rounded-lg transition-colors duration-150 ease-out" title="Remove" data-id="element-3638" data-mp="2rma1fnvqqc57">
                    <TrashIcon className="w-4 h-4" data-id="element-3639" data-mp="2t5boyeq14171" />
                  </button>}
              </div>
            </div>)}
        </div>
      </Card>

      {inviteOpen && <InviteMemberModal member={editingMember} onClose={() => setInviteOpen(false)} onSave={saveMember} data-id="element-3640" data-mp="3kw7ralegqng2" />}
    </div>;
}
export function PaymentsCarts() {
  const {
    orderingEnabled,
    setOrderingEnabled
  } = useBusiness();
  // How customers can pay. A merchant may run online only, cash only, or both
  // — but if they accept orders, at least one has to be on.
  const [online, setOnline] = useState(true);
  const [cash, setCash] = useState(true);
  const [methods, setMethods] = useState({
    card: true,
    applePay: false,
    bank: true
  });
  const [cartEnabled, setCartEnabled] = useState(false);
  const [deposit, setDeposit] = useState(true);
  const lockedLast = (which: 'online' | 'cash') => which === 'online' ? online && !cash : cash && !online;
  return <div className="space-y-5" data-id="element-3641" data-mp="tpqimfij3gts">
      <StepHeading title="Payments & Carts" desc="Choose how customers can pay you. You can start with enquiries only and add payments later." data-id="element-3642" data-mp="tqrwssuucjfq" />

      {/* The master switch. Until this is on, the business only takes enquiries
          and the Orders section stays out of the way entirely. */}
      <Card data-id="element-3643" data-mp="18kn3ptbfryoa">
        <Toggle label="Accept orders and payments" desc="Let customers complete a purchase, booking or rental themselves." checked={orderingEnabled} onChange={setOrderingEnabled} data-id="element-3644" data-mp="1coclnzxlvi0c" />
        <div className="mt-4" data-id="element-3645" data-mp="3eexuld22dq5l">
          <Helper data-id="element-3646" data-mp="3eg7t23ctx0cr">
            {orderingEnabled ? 'Orders now appears in your Business Hub, where you can process and complete every order.' : 'While this is off, customers message you through your AI assistant and your team closes the sale.'}
          </Helper>
        </div>
      </Card>

      <Card className={orderingEnabled ? '' : 'opacity-50 pointer-events-none select-none'} data-id="element-3647" data-mp="ujt3xesbzc2t">
        <BlockLabel data-id="element-3648" data-mp="14jtr5btbmcca">How can customers pay?</BlockLabel>
        <Toggle label="Online payment" desc="Customers pay by card or wallet when they order." checked={online} onChange={v => !lockedLast('online') && setOnline(v)} data-id="element-3649" data-mp="14i8ungng4jbs" />
        <div className="h-px bg-slate-100 my-1" data-id="element-3650" data-mp="1ebziqkfnurqy" />
        <Toggle label="Cash / Pay on Delivery" desc="Customers order now and pay when it is delivered, collected or completed." checked={cash} onChange={v => !lockedLast('cash') && setCash(v)} data-id="element-3651" data-mp="19buxkh3t58ue" />
        <div className="mt-4" data-id="element-3652" data-mp="1i9f2q14sjpaa">
          <Helper data-id="element-3653" data-mp="21xjmhstec4y0">
            {!online && cash ? 'You do not need a payment gateway. Enable Cash / Pay on Delivery and you can start receiving orders straight away.' : online && !cash ? 'Every order is paid for before you process it.' : 'Customers choose at checkout which suits them.'}
          </Helper>
        </div>
      </Card>

      {/* Gateway detail only matters if they actually take money online. */}
      <Card className={orderingEnabled && online ? '' : 'opacity-50 pointer-events-none select-none'} data-id="element-3654" data-mp="1j4z5p9qz1qak">
        <BlockLabel data-id="element-3655" data-mp="199xfeakymi6e">Online payment methods</BlockLabel>
        <div className="space-y-0.5" data-id="element-3656" data-mp="1e7ftefdu3nl9">
          {([{
          key: 'card',
          label: 'Card payments',
          desc: 'Visa, Mastercard and Amex',
          icon: CreditCardIcon
        }, {
          key: 'applePay',
          label: 'Apple Pay & Google Pay',
          desc: 'Fast checkout on mobile',
          icon: SmartphoneIcon
        }, {
          key: 'bank',
          label: 'Bank transfer',
          desc: 'For corporate customers',
          icon: WalletIcon
        }] as const).map(m => <div key={m.key} className="flex items-center gap-3" data-id="element-3657" data-mp="11q6ct1hsy1jz">
              <span className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-3658" data-mp="11rhgz2f5tbbm">
                <m.icon className="w-[18px] h-[18px] text-slate-400" data-id="element-3659" data-mp="s03m57y5kzot" />
              </span>
              <div className="flex-1" data-id="element-3660" data-mp="n1a7vdb1l4nl">
                <Toggle label={m.label} desc={m.desc} checked={methods[m.key]} onChange={v => setMethods(prev => ({
              ...prev,
              [m.key]: v
            }))} data-id="element-3661" data-mp="n2bgzc8hzh2o" />
              </div>
            </div>)}
        </div>
      </Card>

      <Card className={orderingEnabled ? '' : 'opacity-50 pointer-events-none select-none'} data-id="element-3662" data-mp="2ulpi2iipcuiy">
        <BlockLabel data-id="element-3663" data-mp="2ymj5zbw5a9bu">Carts and checkout</BlockLabel>
        <Toggle label="Let customers add items to a cart" desc="Best for shops selling multiple items at once." checked={cartEnabled} onChange={setCartEnabled} data-id="element-3664" data-mp="33mnlaylbgy0h" />
        <div className="h-px bg-slate-100 my-1" data-id="element-3665" data-mp="2azuhg7cmrim9" />
        <Toggle label="Take a deposit to confirm a booking" desc="Customers pay part of the amount up front." checked={deposit} onChange={setDeposit} data-id="element-3666" data-mp="2fy4c8gug0h7c" />
        <div className="mt-4" data-id="element-3667" data-mp="1n9gngbahj9p1">
          <Helper data-id="element-3668" data-mp="1na8i9jw9d3e9">
            A cart suits shops selling several items at once. A deposit suits
            bookings and rentals.
          </Helper>
        </div>
      </Card>
    </div>;
}
export function NotificationsSection() {
  const [prefs, setPrefs] = useState({
    leads: true,
    hot: true,
    messages: true,
    weekly: false
  });
  return <div className="space-y-5" data-id="element-3669" data-mp="384xj4whsdyca">
      <StepHeading title="Notifications" desc="Choose what you want to be alerted about." data-id="element-3670" data-mp="3d1ntd36wtzps" />
      <Card data-id="element-3671" data-mp="3i07itdkpptmw">
        <Toggle label="New leads" desc="When Talab qualifies a new buyer." checked={prefs.leads} onChange={leads => setPrefs(p => ({
        ...p,
        leads
      }))} data-id="element-3672" data-mp="3hzxpbg0gl4cg" />
        <div className="h-px bg-slate-100" data-id="element-3673" data-mp="2ub663e4ujwbt" />
        <Toggle label="Hot leads (90%+ intent)" desc="High-intent buyers ready to purchase." checked={prefs.hot} onChange={hot => setPrefs(p => ({
        ...p,
        hot
      }))} data-id="element-3674" data-mp="2uad74zc4qb48" />
        <div className="h-px bg-slate-100" data-id="element-3675" data-mp="26ksmmc0bv5su" />
        <Toggle label="New messages" desc="Customer replies in Conversations." checked={prefs.messages} onChange={messages => setPrefs(p => ({
        ...p,
        messages
      }))} data-id="element-3676" data-mp="1z2kq8ssee3o5" />
        <div className="h-px bg-slate-100" data-id="element-3677" data-mp="1p6hqftl0qh4d" />
        <Toggle label="Weekly demand report" desc="Search and interest signal summary." checked={prefs.weekly} onChange={weekly => setPrefs(p => ({
        ...p,
        weekly
      }))} data-id="element-3678" data-mp="1k5tr8kmmytz2" />
      </Card>
    </div>;
}
export function PlanBilling() {
  const {
    currentPlanId,
    listingCount,
    PACKAGES,
    subscribed,
    cancelSubscription
  } = useBusiness();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  return <div className="space-y-5" data-id="element-3679" data-mp="ms3uq0atqvbr">
      <StepHeading title="Plan & Billing" desc="Manage your subscription and payment method." data-id="element-3680" data-mp="mt6jo2ol6np1" />
      <Card data-id="element-3681" data-mp="8tnkw0ho0ope">
        <div className="bg-indigo-900 rounded-2xl p-6 text-white mb-6" data-id="element-3682" data-mp="8u6tgqlfe0qr">
          <div className="flex items-center justify-between mb-4" data-id="element-3683" data-mp="dsgu3gpx5tjr">
            <div data-id="element-3684" data-mp="11h9srkxxefi2">
              <p className="text-sm text-indigo-200" data-id="element-3685" data-mp="11grlzudn0ivw">Current Plan</p>
              <p className="text-2xl font-display font-bold" data-id="element-3686" data-mp="15ilh7n52cuoo">
                {currentPlan.name}
              </p>
            </div>
            <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold" data-id="element-3687" data-mp="rq5c8gslqml4">
              Active
            </span>
          </div>

          <div className="mb-4" data-id="element-3688" data-mp="wo6u4yeakfv0">
            <div className="flex items-center justify-between text-sm mb-1.5" data-id="element-3689" data-mp="1kg5jgmaksbr5">
              <span className="text-indigo-100" data-id="element-3690" data-mp="1ke151af1vucm">Listing Usage</span>
              <span className="font-medium" data-id="element-3691" data-mp="1p85djy3ysaft">
                {listingCount} of{' '}
                {currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit}{' '}
                listings
              </span>
            </div>
            <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden" data-id="element-3692" data-mp="289w52dy6bip2">
              <div className="h-full bg-white/80 rounded-full" style={{
              width: `${Math.min(100, listingCount / (currentPlan.listingLimit === Infinity ? Math.max(listingCount, 1) : currentPlan.listingLimit) * 100)}%`
            }} data-id="element-3693" data-mp="23bumrv8knnen" />
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-indigo-100" data-id="element-3694" data-mp="1pm0z3g8sz96i">
            <span data-id="element-3695" data-mp="1kk1tdzplc06e">
              {currentPlan.price === 0 ? 'Free forever' : `AED ${currentPlan.price.toLocaleString()} / month · Renews soon`}
            </span>
            {currentPlan.price > 0 && <button className="text-white hover:text-indigo-200 underline underline-offset-2" data-id="element-3696" data-mp="15jo6uznd679c">
                Update Payment Method
              </button>}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4" data-id="element-3697" data-mp="1fhvlvcy3ef8p">
          <div data-id="element-3698" data-mp="1jl0mf9ym46up">
            <p className="font-bold text-dark-navy" data-id="element-3699" data-mp="1jhctod0zroh1">Need more listings?</p>
            <p className="text-sm text-slate-500" data-id="element-3700" data-mp="1oi0tno777fne">
              Compare plans and unlock a higher listing capacity.
            </p>
          </div>
          <button onClick={() => setUpgradeOpen(true)} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out whitespace-nowrap" data-id="element-3701" data-mp="1y8dezimu7cul">
            {currentPlan.price > 0 ? 'Change Plan' : 'Upgrade Plan'}
          </button>
        </div>

        {(subscribed || currentPlan.price > 0) && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-3702" data-mp="kjbr4arb85p">
            {!cancelOpen ? <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-3703" data-mp="3rrc73rivcd2b">
                <div data-id="element-3704" data-mp="3np7gl1npvwjw">
                  <p className="font-medium text-dark-navy text-sm" data-id="element-3705" data-mp="3nopehoedio42">
                    Cancel subscription
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-3706" data-mp="3irz0d6m4q2la">
                    You'll keep {currentPlan.name} access until the end of your
                    billing period, then move to the Free plan.
                  </p>
                </div>
                <button onClick={() => setCancelOpen(true)} className="px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors duration-150 ease-out whitespace-nowrap" data-id="element-3707" data-mp="aip9d9pj5s4r">
                  Cancel subscription
                </button>
              </div> : <div className="rounded-2xl border border-red-100 bg-red-50 p-4" data-id="element-3708" data-mp="y6p1oe2hpqlg">
                <p className="font-bold text-red-900 text-sm mb-1" data-id="element-3709" data-mp="1h6v6trkjgm7x">
                  Cancel your {currentPlan.name} plan?
                </p>
                <p className="text-sm text-red-700 mb-4" data-id="element-3710" data-mp="18d022knbwxst">
                  Your listing limit will drop to the Free plan (1 listing).
                  Listings beyond the free limit will be paused until you
                  upgrade again.
                </p>

                <div className="mb-4" data-id="element-3711" data-mp="ygddplvsnom1">
                  <label className="block text-sm font-medium text-red-900 mb-2" data-id="element-3712" data-mp="13dnefxomvmp6">
                    Please tell us why you're leaving
                    <span className="text-red-500" data-id="element-3713" data-mp="pf6zjjgmapvy"> *</span>
                  </label>
                  <div className="space-y-2" data-id="element-3714" data-mp="tfz80aho0qr6">
                    {['Too expensive', "I'm not using it enough", 'Missing features I need', 'Switching to another tool', 'Just a temporary pause', 'Other'].map(reason => <label key={reason} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white cursor-pointer transition-colors duration-150 ease-out text-sm ${cancelReason === reason ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-3715" data-mp="85316e3hddhr">
                        <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={e => setCancelReason(e.target.value)} className="accent-red-600" data-id="element-3716" data-mp="qurs7w47o9ru" />
                        <span className="text-slate-700" data-id="element-3717" data-mp="36m5u3pjbp4y3">{reason}</span>
                      </label>)}
                  </div>
                  {cancelReason === 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Tell us more…" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-3718" data-mp="2gqwi9gxplxrq" />}
                  {cancelReason && cancelReason !== 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Anything else you'd like to add? (optional)" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-3719" data-mp="21u5r1uy6vsd7" />}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:justify-end" data-id="element-3720" data-mp="1bjc3i4qarnu9">
                  <button onClick={() => {
              setCancelOpen(false);
              setCancelReason('');
              setCancelNote('');
            }} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-white rounded-lg" data-id="element-3721" data-mp="16ltt09wmt0k5">
                    Keep my plan
                  </button>
                  <button disabled={!cancelReason || cancelReason === 'Other' && !cancelNote.trim()} onClick={() => {
              cancelSubscription();
              setCancelOpen(false);
              setCancelReason('');
              setCancelNote('');
            }} className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors duration-150 ease-out disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3722" data-mp="nt9ddu5y0rya">
                    Submit & cancel
                  </button>
                </div>
              </div>}
          </div>}
      </Card>

      {upgradeOpen && <UpgradePlanModal onClose={() => setUpgradeOpen(false)} data-id="element-3723" data-mp="2nsj270pssciy" />}
    </div>;
}
export function DangerZone() {
  return <div className="space-y-5" data-id="element-3724" data-mp="2is4rtc0q4qhr">
      <StepHeading title="Danger Zone" desc="These actions are permanent." data-id="element-3725" data-mp="2duldyd7xufqc" />
      <div className="bg-red-50 border border-red-100 rounded-2xl p-6 space-y-4" data-id="element-3726" data-mp="28vi7cocb81g9">
        <div className="flex items-center justify-between gap-3" data-id="element-3727" data-mp="24ug7c5akjld8">
          <div data-id="element-3728" data-mp="24w9f5nwx4bmt">
            <p className="font-medium text-red-900" data-id="element-3729" data-mp="24wsxh5qhd21d">Deactivate Business Hub</p>
            <p className="text-sm text-red-700" data-id="element-3730" data-mp="73voxb6hheve">
              Temporarily hide your hub and listings.
            </p>
          </div>
          <button className="px-4 py-2 bg-white border border-red-200 text-red-700 text-sm font-medium rounded-lg hover:bg-red-100 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3731" data-mp="1l0pxtrmlkdj7">
            Deactivate
          </button>
        </div>
        <div className="h-px bg-red-100" data-id="element-3732" data-mp="1urkp5aepa3bi" />
        <div className="flex items-center justify-between gap-3" data-id="element-3733" data-mp="1usvq72grgwyx">
          <div data-id="element-3734" data-mp="1yrlbribydktn">
            <p className="font-medium text-red-900" data-id="element-3735" data-mp="1yrbnbz6k3lq2">Delete Business Hub</p>
            <p className="text-sm text-red-700" data-id="element-3736" data-mp="1235jvfldezdd">
              Permanently remove your hub, catalog, and leads.
            </p>
          </div>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3737" data-mp="172rvjkljw7k2">
            <TrashIcon className="w-4 h-4" data-id="element-3738" data-mp="1fz91m9f25f8y" />
            Delete
          </button>
        </div>
      </div>
    </div>;
}